import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useTranslate from '../../../../Hooks/useTranslate';
import capitalize from '../../../../Helpers/StringsFormatting/capitalize';
import { CenteredFlexBox } from '../../../FlexBox';
import StatTile from '../../charts/StatTile';
import VerticalBars from '../../charts/VerticalBars';
import ReportCard from '../../charts/ReportCard';
import NoData from './NoData';
import DetailList from './DetailList';
import { rate } from './reportUtils';

const fmtPct = (v) => `${(v || 0).toFixed(2)}%`;

/**
 * MobileRedemptionReport — monthly redemption (getRedemption): aggregate
 * redemption % + a per-entity bar list (desktop: practices / answeredNum * 100)
 * with the desktop consistent entity colors, a dashed average line, and a
 * tap-a-bar details panel mirroring the desktop tooltip.
 * See MOBILE_PLAN.md §F(9).
 */
const MobileRedemptionReport = function MobileRedemptionReport({
  redemption = null,
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const mode = theme.palette.mode;
  const [selected, setSelected] = useState(null);

  // Clear the selection whenever the underlying data changes.
  useEffect(() => setSelected(null), [redemption]);

  const redemptionPct = useMemo(() => {
    const items = redemption || [];
    const practices = items.reduce((acc, i) => acc + (i.practices || 0), 0);
    const answered = items.reduce((acc, i) => acc + (i.answeredNum || 0), 0);
    return rate(practices, answered);
  }, [redemption]);

  const answered = useMemo(
    () => (redemption || []).reduce((acc, i) => acc + (i.answeredNum || 0), 0),
    [redemption]
  );

  // Per-entity bars, colored with the desktop consistent-unique color scheme.
  const bars = useMemo(() => {
    const list = redemption || [];
    const colors = theme.custom.generateConsistentUniqueColors(
      list.map((d) => d.entity)
    );
    return list
      .map((d, i) => ({
        label: capitalize(d.entity) ?? d.entity,
        value:
          d.practices && d.answeredNum ? rate(d.practices, d.answeredNum) : 0,
        color: colors[i]?.[mode] ?? colors[i],
        data: d,
      }))
      .sort((a, b) => b.value - a.value);
  }, [redemption]);

  return (
    <>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('REDEMPTION')}
          value={fmtPct(redemptionPct)}
          valueColor="primary.main"
        />
        <StatTile
          label={translate('ANSWERED_CALLS')}
          value={answered.toLocaleString()}
        />
      </CenteredFlexBox>
      <ReportCard title={translate('REDEMPTION')}>
        {bars.length ? (
          <VerticalBars
            items={bars}
            formatValue={fmtPct}
            averageValue={redemptionPct}
            averageLabel={`${fmtPct(redemptionPct)}`}
            onSelect={(item) =>
              item ? setSelected(item.data) : setSelected(null)
            }
            selectedLabel={
              selected ? (capitalize(selected.entity) ?? selected.entity) : null
            }
          />
        ) : (
          <NoData />
        )}
      </ReportCard>

      {selected ? (
        <ReportCard title={selected.entity}>
          <DetailList
            items={[
              {
                caption: translate('REDEMPTION'),
                value: fmtPct(rate(selected.practices, selected.answeredNum)),
              },
              {
                caption: translate('ANSWERED_CALLS'),
                value: (selected.answeredNum || 0).toLocaleString(),
              },
              {
                caption: translate('PRACTICES'),
                value: (selected.practices || 0).toLocaleString(),
              },
              {
                caption: translate('ACCEPTED_PRACTICES'),
                value: (selected.accepted || 0).toLocaleString(),
              },
              {
                caption: translate('RETIRED_PRACTICES'),
                value: (selected.retired || 0).toLocaleString(),
              },
              {
                caption: translate('REFUSED_PRACTICES'),
                value: (selected.refused || 0).toLocaleString(),
              },
              {
                caption: translate('WAITING_PRACTICES'),
                value: (selected.waiting || 0).toLocaleString(),
              },
            ]}
          />
        </ReportCard>
      ) : null}
    </>
  );
};

export default MobileRedemptionReport;
